import { Box, Typography } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { useEffect, useRef, useState } from 'react';

const countUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const StyledStatCard = styled(Box, { shouldForwardProp: (prop) => prop !== 'light' })(({ theme, light }) => ({
  textAlign: 'center',
  padding: theme.spacing(3),
  position: 'relative',

  '&::after': {
    content: '""',
    position: 'absolute',
    right: 0,
    top: '50%',
    transform: 'translateY(-50%)',
    height: '60%',
    width: '1px',
    background: light
      ? 'rgba(255, 255, 255, 0.2)'
      : 'rgba(0, 0, 0, 0.1)',
  },

  '&:last-child::after': {
    display: 'none',
  },

  [theme.breakpoints.down('md')]: {
    '&::after': {
      display: 'none',
    },
    borderBottom: light
      ? '1px solid rgba(255, 255, 255, 0.1)'
      : '1px solid rgba(0, 0, 0, 0.06)',
    '&:last-child': {
      borderBottom: 'none',
    },
  },
}));

const StatNumber = styled(Typography, { shouldForwardProp: (prop) => prop !== 'light' && prop !== 'isVisible' })(({ theme, light, isVisible }) => ({
  fontSize: '3rem',
  fontWeight: 800,
  lineHeight: 1,
  marginBottom: theme.spacing(1),
  background: light
    ? 'linear-gradient(135deg, #FFFFFF 0%, rgba(255, 255, 255, 0.8) 100%)'
    : theme.palette.accent.gradient,
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  opacity: 0,
  ...(isVisible && {
    animation: `${countUp} 0.6s ease-out forwards`,
  }),

  [theme.breakpoints.down('sm')]: {
    fontSize: '2.5rem',
  },
}));

export default function StatCard({ value, suffix = '', label, light = false }) {
  const [isVisible, setIsVisible] = useState(false);
  const [displayValue, setDisplayValue] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.3 }
    );

    const currentRef = ref.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  useEffect(() => {
    if (isVisible) {
      const numericValue = parseInt(value, 10);
      const duration = 1500;
      const steps = 40;
      const stepValue = numericValue / steps;
      let current = 0;

      const timer = setInterval(() => {
        current += stepValue;
        if (current >= numericValue) {
          setDisplayValue(numericValue);
          clearInterval(timer);
        } else {
          setDisplayValue(Math.floor(current));
        }
      }, duration / steps);

      return () => clearInterval(timer);
    }
  }, [isVisible, value]);

  return (
    <StyledStatCard ref={ref} light={light}>
      <StatNumber light={light} isVisible={isVisible}>
        {displayValue}
        {suffix}
      </StatNumber>
      <Typography
        variant="body2"
        sx={{
          color: light ? 'rgba(255, 255, 255, 0.7)' : 'text.secondary',
          fontWeight: 500,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          fontSize: '0.8125rem',
        }}
      >
        {label}
      </Typography>
    </StyledStatCard>
  );
}
